import React, { useState } from "react";
import * as Yup from "yup";
import {
    Col,
    Row,
    Card,
    CardHeader,
} from "reactstrap";
import ReportDetailsHeader from "Components/ReportDetailsHeader";
import { Formik } from "formik";
import apiGlobal from "global/api.global";
import { checkValidObject, compareObjectIgnoreType, dynamicSetFieldErrors, handleServerResponse, useSafeFormikContext } from "GenericForms/Helper";
import env from "environment_system/env_system";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { useDocumentTitle } from "Components/useDocument.hooks";
import FreshWaterStructure from "./FreshWaterStructure";
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction, getCurrentReportFilesAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";
import ErrorComponent from "Components/ErrorComponent";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface FreshWaterType {
    toggleDynamicTabs: any;
    setErrorMessage: any;
}

const FreshWaterComponent = ({
    toggleDynamicTabs,
    setErrorMessage,
}: FreshWaterType) => {
    /** State variables */
    const { previousEnggTabIndex } = useEditReport();
    useDocumentTitle("Fresh Water Report - ecoSAIL");
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const freshWaterCurrentReport = CurrentReport?.data?.chief_engineer?.fresh_water ?? null;
    const freshWaterROBPreviousReport = PreviousReport?.data?.chief_engineer?.report_fw_rob?.rob ?? null;
    const [refreshKey, setRefreshKey] = useState(0);
    const [formRefresh, setFormRefresh] = useState<number>(0);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** State variables end */

    /** Assign values to initial object of Fresh water */
    const getInitialFreshWater = () => {
        if (checkValidObject(editInitialValues?.chief_engineer?.fresh_water)) {
            editInitialValues.chief_engineer.fresh_water.rob = (
                freshWaterROBPreviousReport +
                parseFloat(editInitialValues?.chief_engineer?.fresh_water?.amount_of_fresh_water_produced ?? 0) +
                parseFloat(editInitialValues?.chief_engineer?.fresh_water?.amount_of_fresh_water_received ?? 0) -
                parseFloat(editInitialValues?.chief_engineer?.fresh_water?.amount_of_domestic_water_consumed ?? 0) -
                parseFloat(editInitialValues?.chief_engineer?.fresh_water?.amount_of_distilled_water_consumed ?? 0)
            )?.toFixed(2)
            return editInitialValues?.chief_engineer?.fresh_water;
        }
        if (checkValidObject(freshWaterCurrentReport)) {
            const updatedFreshWater = { ...freshWaterCurrentReport };
            updatedFreshWater.rob = (
                freshWaterROBPreviousReport +
                parseFloat(freshWaterCurrentReport?.amount_of_fresh_water_produced ?? 0) +
                parseFloat(freshWaterCurrentReport?.amount_of_fresh_water_received ?? 0) -
                parseFloat(freshWaterCurrentReport?.amount_of_domestic_water_consumed ?? 0) -
                parseFloat(freshWaterCurrentReport?.amount_of_distilled_water_consumed ?? 0)
            )?.toFixed(2)
            updatedFreshWater.previous_rob = freshWaterROBPreviousReport;
            return updatedFreshWater;
        } else {
            return {
                rob: freshWaterROBPreviousReport,
                previous_rob: freshWaterROBPreviousReport,
                amount_of_fresh_water_produced: null,
                amount_of_fresh_water_received: null,
                amount_of_domestic_water_consumed: null,
                amount_of_distilled_water_consumed: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            };
        }
    };

    /** Fresh water Formik object */
    const FreshWaterFormik = {
        initialValues: getInitialFreshWater(),
        validationSchema: Yup.object().shape({
            rob: Yup.string()
                .matches(/^\d{0,7}(?:\.\d{1,5})?$/, commonValidationMessages.positiveROB).required(commonValidationMessages.required),
            amount_of_fresh_water_produced: Yup.string()
                .matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                .required(commonValidationMessages.required),
            amount_of_fresh_water_received: Yup.string()
                .matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                .required(commonValidationMessages.required),
            amount_of_domestic_water_consumed: Yup.string()
                .matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                .required(commonValidationMessages.required),
            amount_of_distilled_water_consumed: Yup.string()
                .matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                .required(commonValidationMessages.required),
        }),
    };

    /** Calculate fresh water ROB */
    const calculateFreshWaterROB = (
        e: any,
        values: any,
        form: any,
        name: string
    ) => {
        form.setFieldValue("previous_rob", freshWaterROBPreviousReport);
        if (e.target.value === "") {
            e.target.value = 0;
            form.setFieldValue(name, 0);
        }
        form.setFieldValue(
            "rob",
            (
                freshWaterROBPreviousReport +
                parseFloat(e.target.value) +
                parseFloat(values.amount_of_fresh_water_received ?? 0) -
                parseFloat(values.amount_of_domestic_water_consumed ?? 0) -
                parseFloat(values.amount_of_distilled_water_consumed ?? 0)
            )?.toFixed(2)
        );
        if (name === "amount_of_fresh_water_received") {
            form.setFieldValue(
                "rob",
                (
                    freshWaterROBPreviousReport +
                    parseFloat(values.amount_of_fresh_water_produced ?? 0) +
                    parseFloat(e.target.value) -
                    parseFloat(values.amount_of_domestic_water_consumed ?? 0) -
                    parseFloat(values.amount_of_distilled_water_consumed ?? 0)
                )?.toFixed(2)
            );
            setRefreshKey(refreshKey + 1);
        } else if (name === "amount_of_domestic_water_consumed") {
            form.setFieldValue(
                "rob",
                (
                    freshWaterROBPreviousReport +
                    parseFloat(values.amount_of_fresh_water_produced ?? 0) +
                    parseFloat(values.amount_of_fresh_water_received ?? 0) -
                    parseFloat(e.target.value) -
                    parseFloat(values.amount_of_distilled_water_consumed ?? 0)
                )?.toFixed(2)
            );
            setRefreshKey(refreshKey + 1);
        } else if (name === "amount_of_distilled_water_consumed") {
            form.setFieldValue(
                "rob",
                (
                    freshWaterROBPreviousReport +
                    parseFloat(values.amount_of_fresh_water_produced ?? 0) +
                    parseFloat(values.amount_of_fresh_water_received ?? 0) -
                    parseFloat(values.amount_of_domestic_water_consumed ?? 0) -
                    parseFloat(e.target.value)
                )?.toFixed(2)
            );
            setRefreshKey(refreshKey + 1);
        }
        setRefreshKey(refreshKey + 1);
    };

    return (
        <Card className="p-0 mb-0 border-0">
            <CardHeader className="p-2">
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Fresh Water ROB</h4>
                            <p className="card-title-desc pos-start">
                                All readings since last report
                            </p>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {reportingLoading && <FormLoading />}
            {reportingError && <ErrorComponent message="Unable to load required data!" />}
            {!reportingLoading && !reportingError && (
                <Formik
                    onSubmit={async (values: any, actions: any) => {
                        const responseArray: any = [];
                        if (env?.form_validation === false || (compareObjectIgnoreType(FreshWaterFormik.initialValues, values) && values?.id > 0)) {
                            setErrorMessage(null)
                            toggleDynamicTabs(previousEnggTabIndex + 1);
                            return;
                        }
                        actions.setSubmitting(true);
                        /** Fresh water submit */
                        if (checkValidObject(freshWaterCurrentReport) && freshWaterCurrentReport.id > 0) {
                            responseArray.push(apiGlobal
                                .put(`/fresh_water/${values.id}/`, values))
                        } else {
                            responseArray.push(apiGlobal
                                .post(`/fresh_water/`, values))
                        }
                        await handleServerResponse(responseArray).then(async (res) => {
                            if (res === true) {
                                dispatch(getCurrentReportAction(VesselID, ReportID));
                                await dispatch(getCurrentReportFilesAction(ReportID, VesselID));
                                if (env?.form_validation === true) {
                                    setErrorMessage(null)
                                    toggleDynamicTabs(previousEnggTabIndex + 1);
                                }
                            } else {
                                setErrorMessage(res)
                                dynamicSetFieldErrors(res, actions, false)
                            }
                            actions.setSubmitting(false);
                        })
                    }}
                    initialValues={FreshWaterFormik.initialValues}
                    validationSchema={env?.form_validation === true ? FreshWaterFormik.validationSchema : null}
                    key={formRefresh}
                >
                    {() => (
                        <React.Fragment>
                            <FreshWaterStructure
                                FreshWaterFormik={FreshWaterFormik}
                                toggleDynamicTabs={toggleDynamicTabs}
                                setErrorMessage={setErrorMessage}
                                formRefresh={formRefresh}
                                setFormRefresh={setFormRefresh}
                                calculateFreshWaterROB={calculateFreshWaterROB}
                                setEditValues={setEditValues}
                                editValues={editValues}
                                refreshKey={refreshKey}
                                setRefreshKey={setRefreshKey}
                                editInitialValues={editInitialValues}
                                editSubmit={editSubmit}
                                editIsSubmitting={editIsSubmitting}
                            />
                        </React.Fragment>
                    )}
                </Formik>
            )
            }
        </Card >
    );
};

export default FreshWaterComponent;
